import {
    Condition,
    ConditionType,
    createFilterRow
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { getDrillDownNavParams, getNavLinkComponent, getTableIntentLink, } from "@components/drillDown/DrillDown.utils";
import { StatusWarnIcon } from "@components/icon";
import { formatDateToDateString } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { ifAll, IFieldDef, IFieldSettings, IGetValueArgs } from "@components/smart/FieldInfo";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IFormGroupDef, TFormTable } from "@components/smart/smartFormGroup/SmartFormGroup";
import {
    fetchValueHelperData,
    IFetchValueHelperItemsOptions
} from "@components/smart/smartValueHelper/ValueHelper.utils";
import { ICellValueObject } from "@components/table";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { getBoundValue } from "@odata/Data.utils";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import {
    BankTransactionEntity,
    DocumentEntity,
    DocumentGrantAssignmentEntity,
    DocumentItemEntity,
    DocumentItemGrantAssignmentEntity,
    DocumentItemLinkableToGrantViewEntity,
    EntitySetName,
    EntityTypeName,
    GrantAssignmentDivisionEntity,
    GrantBalanceEntity,
    GrantEntity,
    GrantEntryEntity,
    GrantStatusEntity,
    IDocumentGrantAssignmentEntity,
    IDocumentItemEntity,
    IGrantAssignmentDivisionEntity,
    IGrantEntity,
    IGrantEntryEntity,
    InvoiceReceivedEntity, IRegularDocumentItemEntity,
    PaymentDocumentItemGrantAssignmentEntity
} from "@odata/GeneratedEntityTypes";
import {
    BankTransactionTypeCode,
    CurrencyCode,
    DocumentTypeCode,
    FeatureCode,
    GrantAssignmentTypeCode,
    GrantStatusCode
} from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { documentTableAmountFormatter } from "@pages/documents/Document.utils";
import { ClaimRow, LinkWrapper } from "@pages/grant/Grant.styles";
import GrantSplitDialog, { PARTICIPATION_PATH } from "@pages/grant/GrantSplitDialog";
import { IDefinition } from "@pages/PageUtils";
import { getCompanyCurrency, isCashBasisAccountingCompany, isVatRegisteredCompany } from "@utils/CompanyUtils";
import { isGrantEnabledOnCompany } from "@utils/definition.utils";
import { DraftFilterItemKeys, draftFilterLocalPath } from "@utils/DraftUtils";
import { isDefined, isNotDefined, isObjectEmpty, roundToDecimalPlaces } from "@utils/general";
import { getOneFetch } from "@utils/oneFetch";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER, REST_API_URL } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, QueryParam, Sort, TextAlign, ValidatorType } from "../../enums";
import { SmallText } from "../../global.style";
import { TRecordAny, TValue } from "../../global.types";
import { Model } from "../../model/Model";
import { TableStorage } from "../../model/TableStorage";
import { ROUTE_CASH_RECEIPTS, ROUTE_NONPROFIT_GRANT } from "../../routes";
import CurrencyType, { formatCurrency } from "../../types/Currency";
import NumberType from "../../types/Number";
import { FormStorage, IFormStorageDefaultCustomData } from "../../views/formView/FormStorage";
import { IChangedFilter } from "../../views/table/TableView.utils";

export interface IGrantCustomData extends IFormStorageDefaultCustomData {
    pairedDialogOpened?: boolean;
}

export interface IGrantSplitDialogData extends IFormStorageDefaultCustomData {
    isGrantSplitDialogOpened?: boolean;
    GrantSplitDialogBc?: BindingContext;
}

const GrantBasicFilter = {
    select: "IsClosed ne true"
};

function getGrantSelectAdditionalItems(itemHasAmount: boolean, addDefault?: boolean): ISelectItem[] {
    return [...(addDefault ? [{
        id: GrantAssignmentTypeCode.UseDefault,
        label: i18next.t("Document:Grant.UseDefault"),
        groupId: "Default"
    }] : []), {
        id: GrantAssignmentTypeCode.None,
        label: i18next.t("Document:Grant.None"),
        groupId: "Default"
    }, {
        id: GrantAssignmentTypeCode.Division,
        label: i18next.t("Document:Grant.Division"),
        groupId: "Default",
        isDisabled: !itemHasAmount
    }];
}

export function modifyGrantForPaymentTransaction(def: IDefinition): void {
    const hasLinkedDocument = (storage: Model, bc: BindingContext) => {
        const item = storage.getValue(bc.getParentCollection());
        return isDefined(item?.LinkedDocument?.Id);
    };
    const grantPath = createPath(BankTransactionEntity.Items, DocumentItemEntity.GrantAssignment, DocumentItemGrantAssignmentEntity.Grant);
    def.form.fieldDefinition = {
        ...def.form.fieldDefinition,
        [grantPath]: {
            ...def.form.fieldDefinition[grantPath],
            isReadOnly: (args: IGetValueArgs) => {
                return hasLinkedDocument(args.storage, args.bindingContext);
            },
            isDisabled: (args: IGetValueArgs) => {
                return args.data?.BankTransactionTypeCode === BankTransactionTypeCode.IncomingPayment;
            },
            formatter: (_: TValue, args?: IFormatOptions) => {
                const grant = args.storage.getValue(args.bindingContext) as IGrantEntity;
                const isReadOnly = hasLinkedDocument(args.storage, args.bindingContext);
                return isReadOnly ? DASH_CHARACTER : grant?.ShortName;
            }
        }
    };
}

function getGrantSelectFieldSettings(hasDefault: boolean, isItems: boolean): IFieldSettings {
    return {
        groups: [{ id: "Items", title: i18next.t("Grants:Title") }],
        displayName: GrantEntity.ShortName,
        additionalProperties: [{ id: GrantEntity.IsClosed }],
        itemsForRender: (origItems: ISelectItem[], args: IGetValueArgs): ISelectItem[] => {
            let hasAmount = true, hasAmountNet = true;
            const parentCollection = args.bindingContext.getParentCollection();
            if (parentCollection?.getPath(true) === "Items") {
                const amount = args.storage.getValue(args.bindingContext.getParentCollection().navigate(DocumentItemEntity.TransactionAmount));
                hasAmount = isDefined(amount) && amount !== 0;

                const parentPath = parentCollection.getPath(true);
                const isValid = args.storage.isValidPath(`${parentPath}/${DocumentItemEntity.TransactionAmountNet}`);
                if (isValid) {
                    const amountNet = args.storage.getValue(parentCollection.navigate(DocumentItemEntity.TransactionAmountNet));
                    hasAmountNet = isDefined(amountNet) && amountNet !== 0;
                }
            }

            const selectionItems = getGrantSelectAdditionalItems(hasAmount && hasAmountNet, hasDefault);
            const grantItems = origItems?.map(item => {
                return {
                    ...item,
                    groupId: "Items"
                };
            });
            const allItems = [...selectionItems, ...(grantItems ?? [])];

            const hasClosedDivision = !!args.storage.getValue(args.bindingContext.getParent())?.Divisions?.find((division: IGrantAssignmentDivisionEntity) => division.Grant?.IsClosed);
            const isClosed = args.storage.getValue(args.bindingContext)?.IsClosed;
            // root grants without "default" selected items are allowed to change
            const allowClosedGrants = !isItems && !hasAnyItemDefaultGrant(args.storage);

            return allowClosedGrants ? allItems : allItems.map(item => {
                const isDisabled = isClosed || hasClosedDivision || item.additionalData?.IsClosed;
                return isDisabled && item.id !== GrantAssignmentTypeCode.Division ? {
                    ...item,
                    isDisabled: true
                } : item;
            });
        }
    };
}

function getItemTotalAmount(divisionPropBc: BindingContext, storage: FormStorage): number {
    const divisionsBc = divisionPropBc.getParentCollection();
    const itemBc = divisionsBc.getParentCollection();

    const date = storage.data.entity.DateVatDeduction;
    const isVat = isVatRegisteredCompany(storage.context, date);

    const isValid = storage.isValidPath(DocumentItemEntity.TransactionAmountNet); // BankTransactions don't have TransactionAmountNet
    const amountProp = isVat && isValid ? DocumentItemEntity.TransactionAmountNet : DocumentItemEntity.TransactionAmount;
    return storage.getValue(itemBc.navigate(amountProp));
}

const grantComparisonFunction = (origEntity: IDocumentItemEntity, entity: IDocumentItemEntity, bc: BindingContext) => {
    const newAss = getBoundValue({
        bindingContext: bc.getParent(),
        data: entity,
        dataBindingContext: bc.getRootParent()
    }) as IDocumentGrantAssignmentEntity;
    const origAss = getBoundValue({
        bindingContext: bc.getParent(),
        data: origEntity,
        dataBindingContext: bc.getRootParent()
    }) as IDocumentGrantAssignmentEntity;

    const newType = newAss?.Grant?.Id ?? newAss?.GrantAssignmentTypeCode;

    return ((newType) === (origAss?.Grant?.Id ?? origAss?.GrantAssignmentTypeCode)) ||
        (isObjectEmpty(origAss) && (newType === GrantAssignmentTypeCode.None || newType === GrantAssignmentTypeCode.UseDefault));
};

interface IGrantAssignmentDefArgs {
    definition: IDefinition;
    context: IAppContext;
    filterGroupIndex?: number;
    defaultGroupKey?: string;
    isPayment?: boolean;
}

const isGrantClosed = (args: IGetValueArgs) => {
    const grant = args.storage.getValue(args.bindingContext.getParent())?.Grant ?? args.storage.getValue(args.bindingContext);
    return grant?.IsClosed;
};

const shouldDisableGrant = (args: IGetValueArgs) => {
    const parentCollection = args.bindingContext.getParent().getParentCollection();
    const isItems = parentCollection?.getPath(true) === "Items";

    return hasAnyItemDefaultGrant(args.storage) || isItems;
};

export function addGrantAssignmentDef(args: IGrantAssignmentDefArgs): void {
    const def = args.definition;
    const grantPath = createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Grant);
    const hasDefaultItems = !args.isPayment;

    const PercentageDef = {
        label: i18next.t("Grants:Document.Ratio"),
        type: FieldType.NumberInput,
        useForValidation: true,
        isDisabled: ifAll(shouldDisableGrant, isGrantClosed),
        width: BasicInputSizes.S,
        formatter: (val: TValue) => {
            return NumberType.format(val as number, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
        },
        fieldSettings: {
            unit: "%"
        },
        customizationData: {
            useForCustomization: false
        }
    };

    const GrantDef = {
        type: FieldType.ComboBox,
        isRequired: true,
        useForValidation: true,
        filter: GrantBasicFilter,
        isDisabled: ifAll(shouldDisableGrant, isGrantClosed),
        fieldSettings: {
            displayName: GrantEntity.ShortName,
            itemsForRender: (origItems: ISelectItem[], args: IGetValueArgs): ISelectItem[] => {
                const bc = args.bindingContext.getParent().getParent();
                const row = args.storage.getValue(bc);

                return origItems?.map(item => {
                    const foundDivision = row.Divisions?.find((div: IGrantAssignmentDivisionEntity) => div?.Grant?.Id === item.id);

                    return {
                        ...item,
                        isDisabled: foundDivision
                    };
                });
            }
        },
        customizationData: {
            useForCustomization: false
        }
    };

    if (args.defaultGroupKey) {
        def.form.additionalProperties = [
            ...def.form.additionalProperties,
            ...[{ id: createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.GrantAssignmentType) },
                { id: createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Grant, GrantEntity.IsClosed) },
                { id: createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Percentage) },
                { id: createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Grant) },
                { id: createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Amount) }
            ]
        ];

        def.form.fieldDefinition = {
            ...def.form.fieldDefinition,
            [createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Percentage)]: PercentageDef,
            [createPath(DocumentEntity.DefaultGrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Grant)]: GrantDef,
            [grantPath]: {
                type: FieldType.ComboBox,
                additionalProperties: [{ id: GrantEntity.IsClosed }],
                filter: GrantBasicFilter,
                defaultValue: GrantAssignmentTypeCode.None,
                fieldSettings: getGrantSelectFieldSettings(false, false),
                comparisonFunction: grantComparisonFunction
            }
        };

        const group = def.form.groups.find((group: IFormGroupDef) => group.id === args.defaultGroupKey);

        if (shouldAddGrantToDefaultFields(args.context)) {
            group.rows[0].push({ id: grantPath });
        }
    }

    const itemsPath = "Items";
    def.form.additionalProperties = [
        ...def.form.additionalProperties,
        { id: createPath(itemsPath, DocumentItemEntity.GrantAssignment, PaymentDocumentItemGrantAssignmentEntity.GrantAssignmentType) },
        { id: createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Grant, GrantEntity.IsClosed) },
        { id: createPath(itemsPath, DocumentItemEntity.GrantAssignment, GrantAssignmentDivisionEntity.Grant, GrantEntity.IsClosed) }
    ];
    def.form.fieldDefinition = {
        ...def.form.fieldDefinition,
        [createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentItemGrantAssignmentEntity.Grant)]: {
            type: FieldType.ComboBox,
            filter: GrantBasicFilter,
            defaultValue: hasDefaultItems ? GrantAssignmentTypeCode.UseDefault : GrantAssignmentTypeCode.None,
            fieldSettings: getGrantSelectFieldSettings(hasDefaultItems, true),
            isDisabled: (args: IGetValueArgs, otherArgs?: TRecordAny) => {
                const item = args.storage.getValue(args.bindingContext.getParent().getParent());
                const isEmpty = item.TransactionAmount === 0 || item.TransactionAmountNet === 0;

                return isEmpty;
            },
            comparisonFunction: grantComparisonFunction
        },
        [createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Percentage)]: PercentageDef,
        [createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.Grant)]: GrantDef,
        [createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentGrantAssignmentEntity.Divisions, GrantAssignmentDivisionEntity.TransactionAmount)]: {
            label: i18next.t("Grants:Document.Amount"),
            isRequired: true,
            useForValidation: true,
            isDisabled: isGrantClosed,
            formatter: (val: TValue) => {
                return NumberType.format(val as number, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
            },
            validator: {
                type: ValidatorType.Number
            },
            fieldSettings: {
                unit: (args: IGetValueArgs) => {
                    const currencyCode = args.storage.data.entity.TransactionCurrency?.Code ?? CurrencyCode.CzechKoruna;

                    return CurrencyType.getCurrencyUnit(currencyCode);
                }
            },
            customizationData: {
                useForCustomization: false
            }
        },
        [createPath(itemsPath, DocumentItemEntity.GrantAssignment, DocumentGrantAssignmentEntity.Divisions, PARTICIPATION_PATH)]: {
            label: i18next.t("Grants:Document.Participation"),
            isDisabled: true,
            textAlign: TextAlign.Right,
            fieldSettings: {
                unit: (args: IGetValueArgs) => {
                    const currencyCode = args.storage.data.entity.TransactionCurrency?.Code ?? CurrencyCode.CzechKoruna;
                    return CurrencyType.getCurrencyUnit(currencyCode);
                }
            },
            formatter: (_val: TValue, args?: IFormatOptions) => {
                const currentAmount = args.storage.getValue(args.bindingContext.getParent())?.TransactionAmount;
                if (isNotDefined(currentAmount)) {
                    return "";
                }
                const totalAmount = getItemTotalAmount(args.bindingContext, args.storage as FormStorage);
                let total: number;
                if (totalAmount < 0) {
                    total = Math.min(totalAmount - Math.min(currentAmount, 0), 0);
                } else {
                    total = Math.max(totalAmount - Math.max(currentAmount, 0), 0);
                }

                return NumberType.format(total, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
            },
            customizationData: {
                useForCustomization: false
            }
        }
    };

    if (def.table) {
        addGrantFilter(args);
    }
}


export const hasAnyItemDefaultGrant = (storage: Model) => {
    return (storage.data.entity.Items ?? []).some(
        (item: IRegularDocumentItemEntity) => item.GrantAssignment?.GrantAssignmentType?.Code === GrantAssignmentTypeCode.UseDefault
    );
};

export const addGrantFilter = (args: IGrantAssignmentDefArgs) => {
    const def = args.definition;
    if (shouldUseGrant(args.context) && def.table) {
        const index = args.filterGroupIndex ?? 0;
        def.table.filterBarDef[index].filterDefinition = {
            ...def.table.filterBarDef[index].filterDefinition,
            [BindingContext.localContext("Grant")]: getGrantFilterDef(args.isPayment)
        };

        if (shouldAddGrantToDefaultFields(args.context)) {
            def.table.filterBarDef[index].defaultFilters.push(BindingContext.localContext("Grant"));
        }
    }
};

export function handleGrantAssignmentChange(e: ISmartFieldChange, storage: FormStorage<unknown, IGrantSplitDialogData>): boolean {
    const path = e.bindingContext.getPath(true);

    if (e.triggerAdditionalTasks && path === DocumentGrantAssignmentEntity.Grant) {
        const value = e.value;
        if (value === GrantAssignmentTypeCode.Division) {
            storage.setCustomData({
                isGrantSplitDialogOpened: true,
                GrantSplitDialogBc: e.bindingContext
            });
            storage.refresh();
            return true;
        } else {
            // data can be loaded (f.e. corrective invoice) with error and we need to clear division when changed
            const item = storage.getValue(e.bindingContext.getParent());
            item.Divisions = null;
        }
    }

    return false;
}

export const handleDateVatDeductionBlur = (storage: FormStorage, oldDate: Date) => {
    if (shouldUseGrantByType(storage) && shouldUseGrant(storage.context)) {
        const entity = storage.data.entity;
        const wasVatRegistered = isVatRegisteredCompany(storage.context, oldDate);

        const newDate = storage.data.entity.DateVatDeduction;
        const isVatRegistered = isVatRegisteredCompany(storage.context, newDate as Date);

        if (isVatRegistered !== wasVatRegistered) {
            for (const item of entity.Items) {
                const ga = item.GrantAssignment;
                const itemAmount = isVatRegistered ? item.TransactionAmountNet : item.TransactionAmount;
                if (ga) {
                    for (const div of ga.Divisions || []) {
                        div.TransactionAmount = roundToDecimalPlaces(2, itemAmount * (div.Percentage / 100));
                    }
                }
            }
        }
    }
};

export function handleGrantItemAmountChange(args: ISmartFieldChange, storage: FormStorage, isPayment = false): void {
    const relevantProps = ["TransactionAmountNet", "TransactionAmount", "TransactionAmountVat", "Vat"];

    if (relevantProps.includes(args.bindingContext.getPath(true))) {
        const itemBc = args.bindingContext.getParentCollection();
        const grantAssignmentCode = storage.getValue(itemBc.navigate(createPath(DocumentItemEntity.GrantAssignment, DocumentItemGrantAssignmentEntity.GrantAssignmentTypeCode)));
        if (grantAssignmentCode === GrantAssignmentTypeCode.Division) {
            setItemGrantAssignmentToDefault(storage, itemBc, isPayment);
        }
    }
}

function setItemGrantAssignmentToDefault(storage: FormStorage, itemBc: BindingContext, isPayment: boolean, forcedType?: GrantAssignmentTypeCode): void {
    const newType = forcedType ?? (isPayment ? GrantAssignmentTypeCode.None : GrantAssignmentTypeCode.UseDefault);
    const grantAss: IDocumentGrantAssignmentEntity = {
        Divisions: [],
        GrantAssignmentTypeCode: newType,
        GrantAssignmentType: {
            Code: newType,
            Name: i18next.t(`Document:Grant.${newType}`)
        },
        Grant: {
            Id: newType as unknown as number, // in case of default or none we don't have real grant, but we want to set id for easier handling in code
            ShortName: i18next.t(`Document:Grant.${newType}`)
        }
    };
    storage.setValue(itemBc.navigate(DocumentItemEntity.GrantAssignment), grantAss);
}

export function prepareGrantAssignmentsAfterLoad(storage: FormStorage, isPayment?: boolean, collectionName = "Items"): void {
    if (!shouldUseGrant(storage.context) || !shouldUseGrantByType(storage)) {
        return;
    }

    const rootEntity = storage.data.entity;
    const _process = (entity: IEntity, propName: string = DocumentEntity.DefaultGrantAssignment, isItems = false) => {
        const value = entity[propName];
        if (!isObjectEmpty(value)) {
            const type = value[DocumentGrantAssignmentEntity.GrantAssignmentTypeCode];
            if (type === GrantAssignmentTypeCode.None || type === GrantAssignmentTypeCode.UseDefault || type === GrantAssignmentTypeCode.Division) {
                value[DocumentGrantAssignmentEntity.Grant] = {
                    Id: type,
                    ShortName: i18next.t(`Document:Grant.${type}`)
                };
            }
        } else {
            // for perf. reasons, we display null values as 'default' or 'no grant' in combobox (based on where it is used)
            const type = isItems && !isPayment ? GrantAssignmentTypeCode.UseDefault : GrantAssignmentTypeCode.None;
            entity[propName] = {
                [DocumentGrantAssignmentEntity.Grant]: {
                    Id: type,
                    ShortName: i18next.t(`Document:Grant.${type}`)
                },
                [DocumentGrantAssignmentEntity.GrantAssignmentTypeCode]: type
            };
        }
    };

    if (!isPayment) {
        _process(rootEntity);
    }

    for (const item of rootEntity[collectionName] || []) {
        _process(item, DocumentItemEntity.GrantAssignment, true);
    }
}

export function reverseDivisionsAmount(rootEntity: IEntity, collectionName = "Items"): void {
    const _process = (entity: IEntity, propName: string = DocumentEntity.DefaultGrantAssignment) => {
        const value = entity[propName];
        if (value) {
            const type = value[DocumentGrantAssignmentEntity.GrantAssignmentTypeCode];
            if (type === GrantAssignmentTypeCode.Division) {
                for (const division of value.Divisions || []) {
                    division.TransactionAmount = (division.TransactionAmount || 0) * -1;
                    division.Amount = (division.Amount || 0) * -1;
                }
            }
        }
    };

    _process(rootEntity);

    for (const item of rootEntity[collectionName] || []) {
        _process(item, DocumentItemEntity.GrantAssignment);
    }
}


export const shouldUseGrantByType = (storage: FormStorage) => {
    const type = storage.data.bindingContext.getEntityType()?.getName();

    const allowedTypes: string[] = [DocumentTypeCode.InvoiceReceived, DocumentTypeCode.OtherLiability,
        DocumentTypeCode.CorrectiveInvoiceReceived, DocumentTypeCode.BankTransaction, DocumentTypeCode.CashReceiptIssued];

    let shouldUseGrant = allowedTypes.includes(type);
    if (type === EntityTypeName.BankTransaction) {
        shouldUseGrant = storage.getValueByPath(BankTransactionEntity.BankTransactionTypeCode) === BankTransactionTypeCode.OutgoingPayment;
    }

    return shouldUseGrant;
};

export function prepareGrantAssignmentForSave(storage: FormStorage, entity: IEntity, isItem: boolean): void {
    if (!shouldUseGrant(storage.context)) {
        return;
    }
    const propName = isItem ? DocumentItemEntity.GrantAssignment : DocumentEntity.DefaultGrantAssignment;
    const grantType = entity[propName]?.[DocumentGrantAssignmentEntity.Grant]?.Id;
    const type = storage.data.bindingContext.getEntityType()?.getName();
    const isPayment = type === EntityTypeName.BankTransaction || type === EntityTypeName.CashReceiptIssued || type === EntityTypeName.CashReceiptReceived;

    const hasGrant = shouldUseGrantByType(storage);

    if (!hasGrant) {
        entity[propName] = null;
        return;
    }

    if (isObjectEmpty(entity[propName])) {
        // todo: right now GrantAssignement is always loaded, but in case we will change this in future, we want to do this maybe only on new entities
        entity[propName] = {
            Grant: null,
            GrantAssignmentTypeCode: !isPayment && isItem ? GrantAssignmentTypeCode.UseDefault : GrantAssignmentTypeCode.None
        };
        return;
    }

    if (grantType === GrantAssignmentTypeCode.None || grantType === GrantAssignmentTypeCode.Division || grantType === GrantAssignmentTypeCode.UseDefault) {
        entity[propName].Grant = null;
        entity[propName].GrantAssignmentTypeCode = grantType;
        entity[propName].GrantAssignmentType = {
            Code: grantType
        };
    } else if (grantType) {
        entity[propName].GrantAssignmentTypeCode = GrantAssignmentTypeCode.SingleGrant;
        entity[propName].GrantAssignmentType = {
            Code: GrantAssignmentTypeCode.SingleGrant
        };
    }

    if (grantType && isItem && grantType !== GrantAssignmentTypeCode.None && grantType !== GrantAssignmentTypeCode.UseDefault) {
        for (const division of entity[DocumentItemEntity.GrantAssignment]?.Divisions || []) {
            division.TransactionAmount = roundToDecimalPlaces(2, division.TransactionAmount);

            const exRate = storage.data.entity.ExchangeRatePerUnit ?? 1;
            division.Amount = roundToDecimalPlaces(2, division.TransactionAmount * exRate);
        }
    }

    if (grantType !== GrantAssignmentTypeCode.Division) {
        entity[propName].Divisions = null;
    }
}

export const getGrantFilterDef = (isPayment: boolean) => {
    return {
        label: i18next.t("Grants:Title"),
        isValueHelp: false,
        type: FieldType.MultiSelect,
        fieldSettings: {
            entitySet: EntitySetName.Grants,
            idName: "ShortName",
            displayName: "ShortName",
        },
        filter: {
            buildFilter: (filter: IChangedFilter) => {
                const filters = filter.value as TRecordAny[];
                if (filters?.length > 0) {
                    const values = filters.map(filter => filter.value ?? filter);
                    const names = `(${values?.map(val => `'${val}'`)?.join(',')})`;

                    const conditions: string[] = [];

                    if (!isPayment) {
                        conditions.push(`DefaultGrantAssignment/Grant/ShortName in ${names}`);
                        conditions.push(`DefaultGrantAssignment/Divisions/any(d: d/Grant/ShortName in ${names})`);
                    }

                    conditions.push(`Items/any(item: item/GrantAssignment/Grant/ShortName in ${names})`);
                    conditions.push(`Items/any(i: i/GrantAssignment/Divisions/any(d: d/Grant/ShortName in ${names}))`);
                    return `(${conditions.join(' OR ')})`;
                }

                return "";
            }
        }
    };
};


export const renderGrantSplitDialog = (storage: FormStorage<unknown, IGrantSplitDialogData>) => {
    const isOpened = storage.getCustomData().isGrantSplitDialogOpened;

    if (!isOpened) {
        return null;
    }

    const dialogBc = storage.getCustomData().GrantSplitDialogBc;
    const bc = dialogBc.getParent().navigate(DocumentGrantAssignmentEntity.Divisions);
    const isCollection = dialogBc.getParent().getParent().isInCollection();

    return <GrantSplitDialog isItems={isCollection} storage={storage} bindingContext={bc}/>;
};

interface IAmountFormatterArgs {
    itemPath: string;
    storage: Model;
    context: IAppContext;
}

const amountFormatter = (args: IAmountFormatterArgs, formatterArgs: IFormatOptions, useVat?: boolean) => {
    const amountSuffix = useVat ? "Net" : "";
    const entity = formatterArgs.entity as IGrantEntryEntity;
    const documentItem = entity[args.itemPath as keyof IGrantEntryEntity] as IDocumentItemEntity;
    const currencyCode = entity.CurrencyCode ?? getCompanyCurrency(args.context);
    const formattedAmount = formatCurrency(documentItem[`Amount${amountSuffix}`] || 0, currencyCode);

    if (entity.TransactionCurrencyCode && entity.TransactionCurrencyCode !== currencyCode) {
        const formattedTransactionAmount = formatCurrency(documentItem[`TransactionAmount${amountSuffix}`] || 0, entity.TransactionCurrencyCode);

        return {
            value: <><SmallText>{formattedAmount} | </SmallText>{formattedTransactionAmount}</>,
            tooltip: `${formattedAmount} | ${formattedTransactionAmount}`
        };
    }

    return formattedAmount;
};

const getAmountVatCol = (args: IAmountFormatterArgs): IFieldDef => {
    return {
        id: createPath(args.itemPath, DocumentItemEntity.AmountNet),
        label: i18next.t("Grants:FormTable.AmountNoVat"),
        additionalProperties: [
            { id: `/${GrantEntryEntity.TransactionCurrencyCode}` },
            { id: DocumentItemEntity.TransactionAmountNet }
        ],
        formatter: (val: TValue, formatterArgs: IFormatOptions) => {
            return amountFormatter(args, formatterArgs, true);
        }
    };
};

const getAmountCol = (args: IAmountFormatterArgs): IFieldDef => {
    return {
        id: createPath(args.itemPath, DocumentItemEntity.Amount),
        label: i18next.t("Grants:FormTable.Amount"),
        additionalProperties: [
            { id: `/${GrantEntryEntity.TransactionCurrencyCode}` },
            { id: DocumentItemEntity.TransactionAmount }
        ],
        formatter: (val: TValue, formatterArgs: IFormatOptions) => {
            return amountFormatter(args, formatterArgs, false);
        }
    };
};

export const getDocumentGrantTab = (context: IAppContext, isPaymentDocument?: boolean): IFormGroupDef => {
    const itemPath = isPaymentDocument ? GrantEntryEntity.PaymentDocumentItem : GrantEntryEntity.DocumentItem;
    return {
        id: `grants`,
        title: i18next.t("Document:Form.GrantEntries"),
        // grant entry is to either Document type or PaymentDocument type
        table: {
            id: "grantsTable",
            entitySet: EntitySetName.GrantEntries,
            parentKey: isPaymentDocument ? "PaymentDocument/Id" : "Document/Id",
            columns: [{
                id: createPath(itemPath, "Order"),
                label: i18next.t("Grants:FormTable.Item")
            }, GrantTabCol, {
                id: createPath(itemPath, DocumentItemEntity.Amount),
                factory: (storage: Model, id?: string) => {
                    const date = storage.data.entity?.DateVatDeduction;
                    const useVat = isVatRegisteredCompany(context, date) && !isPaymentDocument;
                    const args = { itemPath, storage, context };

                    return Promise.resolve([useVat ? getAmountVatCol(args) : getAmountCol(args)]);
                }
            }, {
                id: GrantEntryEntity.Amount,
                label: i18next.t("Grants:FormTable.PaidByGrant"),
                additionalProperties: [
                    { id: `/${GrantEntryEntity.TransactionCurrencyCode}` },
                    { id: `/${GrantEntryEntity.CurrencyCode}` },
                    { id: `/${GrantEntryEntity.TransactionAmount}` },
                ],
                formatter: (val: TValue, args: IFormatOptions) => documentTableAmountFormatter(args.entity, args.context)
            }, {
                id: BindingContext.localContext("Participation"),
                label: i18next.t("Grants:FormTable.Participation"),
                textAlign: TextAlign.Right,
                additionalProperties: [
                    { id: GrantEntryEntity.Amount },
                    { id: GrantEntryEntity.TransactionAmount },
                    { id: GrantEntryEntity.CurrencyCode },
                    { id: GrantEntryEntity.TransactionCurrencyCode },
                    { id: createPath(itemPath, DocumentItemEntity.TransactionAmount) },
                    { id: createPath(itemPath, DocumentItemEntity.Amount) },
                    ...(!isPaymentDocument ?
                        [{ id: createPath(itemPath, DocumentItemEntity.TransactionAmountNet) }, { id: createPath(itemPath, DocumentItemEntity.AmountNet) }] : [])
                ],
                formatter: (_: TValue, args: IFormatOptions) => {
                    const date = args.storage.data.entity?.DateVatDeduction;
                    const useVat = isVatRegisteredCompany(context, date) && !isPaymentDocument;
                    const entity = args.entity as IGrantEntryEntity;
                    const documentItem: IDocumentItemEntity = entity[itemPath];
                    const amountSuffix = useVat ? "Net" : "";
                    const entryAmount = entity.Amount;
                    const entryTransactionAmount = entity.TransactionAmount;
                    const currencyCode = entity.CurrencyCode ?? getCompanyCurrency(context);
                    const formattedAmount = formatCurrency(documentItem[`Amount${amountSuffix}`] - entryAmount, currencyCode);

                    if (entity.TransactionCurrencyCode && entity.TransactionCurrencyCode !== currencyCode) {
                        const formattedTransactionAmount = formatCurrency(documentItem[`TransactionAmount${amountSuffix}`] - entryTransactionAmount, entity.TransactionCurrencyCode);

                        return {
                            value: <><SmallText>{formattedAmount} | </SmallText>{formattedTransactionAmount}</>,
                            tooltip: `${formattedAmount} | ${formattedTransactionAmount}`
                        };
                    }
                    return formattedAmount;
                }
            }],
            initialSortBy: [
                { id: `${itemPath}/Order`, sort: Sort.Asc }
            ]
        }
    };
};

const DOCUMENT_TABLES_GROUP_ID = "Tabs";
export const addGrantTableToDocumentDef = (def: IDefinition, context: IAppContext, isPayment?: boolean) => {
    const group = def.form.groups.find(group => group.id === DOCUMENT_TABLES_GROUP_ID);
    if (group) {
        group.tabs.push(getDocumentGrantTab(context, isPayment));
    }
};

export const addGrantClaimsTableToDocumentDef = (def: IDefinition, context: IAppContext, isPayment?: boolean) => {
    const group = def.form.groups.find(group => group.id === DOCUMENT_TABLES_GROUP_ID);
    if (group) {
        group.tabs.push(getDocumentClaimGrantTab());
    }
};

const GrantTabCol = {
    id: GrantEntryEntity.Grant,
    fieldSettings: {
        displayName: GrantEntity.ShortName
    },
    additionalProperties: [
        { id: GrantEntity.Id }
    ],
    formatter: (_: TValue, args: IFormatOptions) => {
        const entity = args.entity as IGrantEntryEntity;
        const grant = entity.Grant;
        if (isObjectEmpty(grant)) {
            return DASH_CHARACTER;
        }
        return getTableIntentLink(grant.ShortName, {
            route: `${ROUTE_NONPROFIT_GRANT}/${grant.Id}`,
            context: args.storage.context,
            storage: args.storage
        });
    }
};

const getDocumentClaimGrantTab = () => {
    return {
        id: `grantsClaims`,
        title: i18next.t("Document:Form.Grant"),
        table: {
            id: "grantsTable",
            entitySet: EntitySetName.GrantClaims,
            parentKey: "Document/Id",
            columns: [{
                id: createPath(GrantEntryEntity.DocumentItem, "Order"),
                label: i18next.t("Grants:FormTable.Item")
            }, GrantTabCol, {
                id: createPath(GrantEntryEntity.Grant, GrantEntity.Balance, GrantBalanceEntity.Status),
                fieldSettings: {
                    displayName: GrantStatusEntity.Name
                },
            }]
        }
    };
};

export const loadPairAccounts = async (storage: TableStorage) => {
    const filterFetch = getOneFetch();

    const bindingContext = storage.data.bindingContext.navigate(DocumentItemLinkableToGrantViewEntity.CreditAccount);
    const fieldInfo = storage.getInfo(bindingContext);
    const opts: IFetchValueHelperItemsOptions = {
        storage: storage,
        bindingContext, fieldInfo,
        isEnum: false
    };

    const { data } = await fetchValueHelperData(opts, filterFetch);
    const set: Set<string> = new Set();
    for (const item of (data ?? [])) {
        set.add(item.CreditAccount);
        set.add(item.DebitAccount);
    }

    return set;
};

export const DEFAULT_GRANT_ACCOUNTS = ["346", "347", "348", "378", "379"];

export const startsWithAccount = (key: string) => {
    return DEFAULT_GRANT_ACCOUNTS.some(defaultKey => {
        return key?.startsWith(defaultKey);
    });
};

export const setGrantAfterAmountBlur = (storage: FormStorage, bc: BindingContext) => {
    const path = bc.getPath();
    const hasGrant = shouldUseGrantByType(storage);
    if (hasGrant && (path === DocumentItemEntity.TransactionAmount || path === DocumentItemEntity.TransactionAmountNet)) {
        const itemBc = bc.getParent();
        const value = storage.getValue(bc);
        const item = storage.getValue(itemBc);
        if (value === 0 && item?.[DocumentItemEntity.GrantAssignment]) {
            setItemGrantAssignmentToDefault(storage, itemBc, false, GrantAssignmentTypeCode.None);
        }

        storage.addActiveField(itemBc.navigate(DocumentItemEntity.GrantAssignment));
    }
};

export const getThemeColorForGrantStatus = (grantStatus: GrantStatusCode) => {
    switch (grantStatus) {
        case GrantStatusCode.Applied:
            return "C_SEM_text_warning";
        case GrantStatusCode.Closed:
            return "C_SEM_text_bad";
        case GrantStatusCode.InProgress:
            return "C_SEM_text_good";
    }
};

export const shouldUseGrant = (context: IAppContext) => {
    return !isCashBasisAccountingCompany(context) && isGrantEnabledOnCompany(context.getCompany());
};

export const getDraftDocsUsedByGrantDef = (): TFormTable => {
    return {
        id: "docsUsedByGrantTable",
        parentKey: "GrantId",
        endPoint: "/api/rest/GrantClosing/GetDraftsUsedByGrant",
        initialSortBy: [{ id: "DocumentType", sort: Sort.Asc }],
        columns: [{
            id: "DocumentType",
            label: i18next.t("Grants:Form.Type"),
            fieldSettings: {
                displayName: "Name"
            }
        }, {
            id: "DocumentDraftModifiedCount",
            label: i18next.t("Grants:Form.UnsavedVersion"),
            textAlign: TextAlign.Right,
            formatter: grantModifiedDocLinkFormatter
        }, {
            id: "DocumentDraftCount",
            label: i18next.t("Grants:Form.WorkVersion"),
            textAlign: TextAlign.Right,
            formatter: grantDraftLinkFormatter
        }]
    };
};

const createModifiedDocFilter = (args: IFormatOptions) => {
    const filters: TRecordAny = {
        [draftFilterLocalPath]: [createFilterRow({
            condition: Condition.Equals,
            value: [DraftFilterItemKeys.UnsavedChanges]
        })]
    };

    return filters;
};

const createGrantFilter = (args: IFormatOptions) => {
    const shortName = args.storage.data.entity.ShortName;

    const filters: TRecordAny = {
        [BindingContext.localContext("Grant")]: [createFilterRow({
            condition: Condition.Equals,
            value: shortName
        })]
    };

    return filters;
};

const createGrantPeriodFilter = (args: IFormatOptions, isIn?: boolean) => {
    const entity = args.storage.data.entity;
    const start = entity.DateStart;
    const end = entity.DateEnd;
    const filters: TRecordAny = {};

    const docType = args.item.DocumentType?.Code;
    const propsName = docType === DocumentTypeCode.BankTransaction ? BankTransactionEntity.DateBankTransaction :
        InvoiceReceivedEntity.DateIssued;

    if (start && end) {
        filters[propsName] = [createFilterRow({
            condition: Condition.Between,
            type: isIn ? ConditionType.Included : ConditionType.Excluded,
            value: {
                from: formatDateToDateString(start),
                to: formatDateToDateString(end)
            }
        })];
    } else if (start) {
        filters[propsName] = [createFilterRow({
            condition: isIn ? Condition.IsAfterOrEqualsTo : Condition.IsBeforeOrEqualsTo,
            value: formatDateToDateString(start)
        })];
    } else if (end) {
        filters[propsName] = [createFilterRow({
            condition: isIn ? Condition.IsBeforeOrEqualsTo : Condition.IsAfterOrEqualsTo,
            value: formatDateToDateString(end)
        })];
    }

    return filters;
};

const grantLinkFormatter = (val: TValue, args: IFormatOptions) => {
    return createDrillDownLink(val, args, createGrantFilter(args));
};

const grantDraftLinkFormatter = (val: TValue, args: IFormatOptions) => {
    return createDrillDownLink(val, args, createGrantFilter(args), true);
};

const grantModifiedDocLinkFormatter = (val: TValue, args: IFormatOptions) => {
    return createDrillDownLink(val, args, {
        ...createGrantFilter(args),
        ...createModifiedDocFilter(args)
    }, false, true);
};


const grantInPeriodLinkFormatter = (val: TValue, args: IFormatOptions) => {
    return createDrillDownLink(val, args, {
        ...createGrantFilter(args),
        ...createGrantPeriodFilter(args, true)
    });
};

const grantOutOfPeriodLinkFormatter = (val: TValue, args: IFormatOptions): ICellValueObject => {
    return createDrillDownLink(val, args, {
        ...createGrantFilter(args),
        ...createGrantPeriodFilter(args, false)
    }, false, true);
};


const createDrillDownLink = (val: TValue, args: IFormatOptions, filters: TRecordAny, isDraft = false, addIcon = false) => {
    const dt = args.item.DocumentType?.Code ?? args.item.DocumentTypeCode;
    let route = getRouteByDocumentType(dt);
    if (dt === DocumentTypeCode.CashReceiptReceived || dt === DocumentTypeCode.CashReceiptIssued) {
        route = ROUTE_CASH_RECEIPTS;
    }

    const _isPayment = dt === DocumentTypeCode.BankTransaction || dt === DocumentTypeCode.CashReceiptIssued || dt === DocumentTypeCode.CashReceiptReceived;

    const link = getNavLinkComponent(val as string, {
        route,
        storage: args.storage,
        filters,
        customQueryString: isDraft ? "drafts=true" : _isPayment ? `${QueryParam.DefaultTab}=null` : ``,
        context: args.storage.context
    }, getDrillDownNavParams);

    const hasValue = val !== 0;
    const showIcon = (hasValue) && (addIcon || isDraft);
    const value = hasValue ? link : val;

    return {
        tooltip: val as string,
        value: showIcon ?
            <ClaimRow><StatusWarnIcon width="16px" height="16px"/><LinkWrapper>{value}</LinkWrapper></ClaimRow> : value
    };
};

export const shouldAddGrantToDefaultFields = (context: IAppContext) => {
    return isGrantEnabledOnCompany(context.getCompany());
};

export const getDocsUsedByGrantDef = (): TFormTable => {
    const _formatCurrency = (val: TValue, args: IFormatOptions) => {
        const currency = args.item?.CurrencyCode || CurrencyCode.CzechKoruna;
        return formatCurrency(val, currency);
    };

    return {
        id: "docsUsedByGrantTable",
        parentKey: "GrantId",
        endPoint: `${REST_API_URL}/GrantClosing/GetDocumentsUsedByGrant`,
        columns: [{
            id: "DocumentType",
            label: i18next.t("Grants:Form.Type"),
            fieldSettings: {
                displayName: "Name"
            }
        }, {
            id: "DocumentCount",
            label: i18next.t("Grants:Form.DocumentCount"),
            textAlign: TextAlign.Right,
            formatter: grantLinkFormatter
        }, {
            id: "DocumentCountInPeriod",
            textAlign: TextAlign.Right,
            label: i18next.t("Grants:Form.DocumentCountInPeriod"),
            formatter: grantInPeriodLinkFormatter

        }, {
            id: "DocumentCountOutOfPeriod",
            textAlign: TextAlign.Right,
            label: i18next.t("Grants:Form.DocumentCountOutOfPeriod"),
            formatter: grantOutOfPeriodLinkFormatter
        }, {
            label: i18next.t("Grants:Form.GrantAmount"),
            id: "Amount",
            textAlign: TextAlign.Right,
            formatter: _formatCurrency
        }, {
            label: i18next.t("Grants:Form.GrantEntryAmount"),
            id: "GrantEntryAmount",
            textAlign: TextAlign.Right,
            formatter: _formatCurrency
        }, {
            label: i18next.t("Grants:Form.ParticipationAmount"),
            id: "ParticipationAmount",
            textAlign: TextAlign.Right,
            formatter: _formatCurrency
        }]
    };
};